import { useEffect, useRef } from 'react';
import { Organization, OrgInventoryItem, Quote, QuoteCrewMember, QuotePatch, ResidentialFlatRates, ResourceRate, ResourceSlug } from '../types/admin';
import { resolveOrgTravelTimeRate, resolveOrgEfficiencyMultiplier, resolveOrgFixedTravelTimeHours, formulaResultKey } from '@iomi/shared';
import { buildCrewFromOrgRates, computeFormula, computeResidentialFlatRateFormula, type AvailableTruckType } from '../utils/formulaCalculation';
import {
  checkFormulaReadiness,
  FormulaReadiness,
  getFormulaFingerprint,
} from '../utils/formulaReadiness';

interface Args {
  quote: Quote | null | undefined;
  orgItems: OrgInventoryItem[];
  orgItemsLoading: boolean;
  orgRates: ResourceRate[];
  orgRatesLoading: boolean;
  org: Organization | null | undefined;
  orgSettingsLoading: boolean;
  updateQuote: (patch: QuotePatch) => Promise<Quote>;
  orgFlatRates?: ResidentialFlatRates | null;
  orgFlatRatesLoading?: boolean;
  availableTruckTypes?: AvailableTruckType[];
}

/**
 * Fingerprint of the inputs the engine last computed (or found already
 * satisfied) for each quote, shared by every mounted instance of the hook.
 * A per-instance ref let two instances (or one instance across a StrictMode
 * remount) each save the same formula before either saw the stored
 * fingerprint; keying the guard by quote id makes the second one skip.
 */
const engineFingerprintByQuote = new Map<string, string>();

/**
 * Identity (see formulaResultKey) of the result each quote last had computed
 * and sent, or found already stored, this session. Lets a second computation
 * that lands while the first save is still in flight recognise it would write
 * the same numbers and skip, even though quote.formula is still empty.
 */
const engineResultByQuote = new Map<string, string>();

/**
 * Reactively computes and persists the formula whenever formula inputs change
 * and all readiness criteria are met. Only runs for draft quotes.
 *
 * Triggers recomputation when any of these change:
 *   - Origin or destination logistics
 *   - Move timing
 *   - Origin or destination address (and transit)
 *   - Origin or destination warehouse / travel time to job
 *   - Casegoods inventory (items added/removed/quantity changed)
 *   - Modular workstations
 *   - Crew rates
 *
 * Uses a fingerprint of all formula inputs so that saving the formula back to
 * the quote (which changes quote.formula but NOT the inputs) does not cause
 * an infinite recomputation loop. Saves at most once per input change across
 * every mounted instance, and not at all when the recomputed formula equals
 * the stored one.
 *
 * Returns readiness info for UI feedback (what's still needed to run the engine).
 */
export function useFormulaEngine({
  quote,
  orgItems,
  orgItemsLoading,
  orgRates,
  orgRatesLoading,
  org,
  orgSettingsLoading,
  updateQuote,
  orgFlatRates,
  orgFlatRatesLoading,
  availableTruckTypes,
}: Args): FormulaReadiness {
  const effectiveCrew = quote?.crew ?? buildCrewFromOrgRates(orgRates);
  // Readiness is evaluated against the same org settings the engine prices
  // with, so a $0 rate the quote would bill (or a $0 / disabled flat-rate
  // tier) blocks the run instead of producing a $0 estimate.
  const readiness = checkFormulaReadiness(quote, {
    effectiveCrew,
    useBlendedTruckDriverRate: org?.useBlendedTruckDriverRate,
    residentialPricingModel: org?.residentialPricingModel,
    flatRates: orgFlatRatesLoading ? undefined : orgFlatRates,
  });
  const fingerprint = getFormulaFingerprint(quote);

  // Kept for the error path: a failed save clears the shared guard so the
  // next render retries, but only if this instance was the one that set it.
  const triggeredFingerprintRef = useRef<string>('');

  // Determine if this quote should use the flat-rate formula path
  const isFlatRateQuote = readiness.isFlatRate;

  useEffect(() => {
    if (!quote || quote.quoteStatus !== 'draft') return;
    if (orgItemsLoading || orgRatesLoading || orgSettingsLoading) return;
    if (isFlatRateQuote && (orgFlatRatesLoading || !orgFlatRates)) return;
    // Flat-rate readiness only asks for a bedroom count and a priced, enabled
    // tier — strides, timing and inventory are still not required there.
    if (!readiness.ready) return;

    // Skip if the stored fingerprint matches: the formula is already up-to-date for
    // these inputs. This prevents re-running when navigating back to this page and
    // preserves manual breakdown adjustments (hours, crew quantities, etc.).
    if (quote.formula?.formulaInputsFingerprint === fingerprint) {
      engineFingerprintByQuote.set(quote.id, fingerprint);
      return;
    }

    // Skip if any instance already computed these inputs this session (in flight,
    // or found unchanged).
    if (engineFingerprintByQuote.get(quote.id) === fingerprint) return;

    engineFingerprintByQuote.set(quote.id, fingerprint);
    triggeredFingerprintRef.current = fingerprint;

    const effectiveCrew = quote.crew ?? buildCrewFromOrgRates(orgRates);
    const useBlendedRate = org?.useBlendedTruckDriverRate ?? false;

    const effectiveFixedHours = resolveOrgFixedTravelTimeHours(org, quote.type);

    const effectiveMultiplier = quote.efficiencyMultiplier ?? resolveOrgEfficiencyMultiplier(org, quote.type);
    let formula;
    if (isFlatRateQuote && orgFlatRates) {
      formula = computeResidentialFlatRateFormula(
        quote,
        orgFlatRates,
        quote.travelTimeRate ?? resolveOrgTravelTimeRate(org, quote.type),
        effectiveCrew,
        useBlendedRate,
        effectiveFixedHours,
        orgItems,
      );
    } else {
      formula = computeFormula(quote, orgItems, quote.travelTimeRate ?? resolveOrgTravelTimeRate(org, quote.type), effectiveCrew, effectiveMultiplier, useBlendedRate, effectiveFixedHours, availableTruckTypes);
    }

    // Compute per-resource quantities from the freshly computed formula and persist
    // them so the breakdown page can display and preserve manual edits.
    const isRes = quote.type === 'residential';
    const supervisorQty = isRes ? 1 : (formula.method === 'Shuttle' ? 2 : 1);

    let qtyMap: Partial<Record<ResourceSlug, number>>;
    if (useBlendedRate) {
      const truckDriverQty = formula.truckQuantity;
      const moverQty = Math.max(0, formula.finalCrewSize - supervisorQty - truckDriverQty);
      qtyMap = { supervisor: supervisorQty, truck_driver: truckDriverQty, mover: moverQty };
    } else if (isRes) {
      const moverQty = Math.max(0, formula.finalCrewSize - supervisorQty);
      qtyMap = { supervisor: supervisorQty, mover: moverQty, truck: formula.truckQuantity };
    } else {
      const formulaDriverQty = formula.driverQuantity ?? formula.truckQuantity;
      const moverQty = Math.max(0, formula.finalCrewSize - supervisorQty - formulaDriverQty);
      qtyMap = { supervisor: supervisorQty, driver: formulaDriverQty, mover: moverQty, truck: formula.truckQuantity };
    }
    const updatedCrew: QuoteCrewMember[] = effectiveCrew
      // For blended rate, filter out separate 'driver' and 'truck' crew members
      // For residential (non-blended), filter out 'driver' since supervisor drives
      .filter(c => {
        if (useBlendedRate) return c.slug !== 'driver' && c.slug !== 'truck';
        if (isRes) return c.slug !== 'driver';
        return true;
      })
      .map(c => ({
        ...c,
        quantity: qtyMap[c.slug] ?? 0,
      }));

    // Stamping the org-default multiplier onto the quote changes the fingerprint
    // (it includes efficiencyMultiplier), so store the fingerprint of the quote
    // as it will be after this save; otherwise the saved formula immediately
    // reads as stale and gets computed and written a second time.
    const stampsMultiplier = !isFlatRateQuote && quote.efficiencyMultiplier === undefined;
    const fingerprintAfterSave = stampsMultiplier
      ? getFormulaFingerprint({ ...quote, efficiencyMultiplier: effectiveMultiplier })
      : fingerprint;

    // Inputs changed in a way that did not move a single number (an address
    // re-saved with the same place, a transit re-calculated on load to the
    // same or an equivalent time): nothing to write. Compare against what the
    // quote holds AND against what this session last sent for the quote, since
    // quote.formula is still empty while the first save is in flight.
    const resultKey = formulaResultKey(formula, updatedCrew);
    const storedKey = quote.formula ? formulaResultKey(quote.formula, quote.crew) : undefined;
    if (!stampsMultiplier && (resultKey === storedKey || resultKey === engineResultByQuote.get(quote.id))) {
      engineResultByQuote.set(quote.id, resultKey);
      return;
    }
    engineResultByQuote.set(quote.id, resultKey);

    const patch: QuotePatch = {
      formula: { ...formula, formulaInputsFingerprint: fingerprintAfterSave },
      crew: updatedCrew,
    };
    if (stampsMultiplier) {
      patch.efficiencyMultiplier = effectiveMultiplier;
    }

    updateQuote(patch).catch((err) => {
      // Reset so the next render retries.
      if (engineFingerprintByQuote.get(quote.id) === triggeredFingerprintRef.current) {
        engineFingerprintByQuote.delete(quote.id);
        engineResultByQuote.delete(quote.id);
      }
      triggeredFingerprintRef.current = '';
      console.error('[useFormulaEngine] save failed', err);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, readiness.ready, orgItemsLoading, orgRatesLoading, orgSettingsLoading, orgFlatRatesLoading, isFlatRateQuote]);

  return readiness;
}
